import { format } from 'date-fns';
import isEmpty from 'lodash-es/isEmpty';
import { useMemo } from 'react';
import { EMPTY_DEFAULT } from '@/common/components/shared/table';
import { EpmVdpSourceType } from '@/common/graphql';
import { ContractTypeLabels } from '@/common/types';
import { DateUtils } from '@/common/utils';
import type { DashboardEmployee } from '../../types';
import { Detail } from './Detail';
import { EmployeeDetailsLoader } from './EmployeeDetailsLoader';

export const EmployeeDetails = ({ employee }: { employee?: DashboardEmployee | null }) => {
  const { text: hiredDateText, isFuture } = useMemo(
    () => DateUtils.getDifferenceString(employee?.generalInformation.hiredDate),
    [employee?.generalInformation.hiredDate],
  );

  if (!employee) {
    return <EmployeeDetailsLoader />;
  }

  return (
    <div className="flex flex-col gap-4 md:flex-row lg:gap-8">
      <div className="flex flex-col items-center justify-center gap-6 rounded-2xl border p-4 md:max-w-[260px]">
        <img
          alt={employee.generalInformation?.preferredFullName}
          className="h-[128px] w-[128px] rounded-full"
          src={employee.pictureUrl ?? undefined}
        />
        <h2 className="text-center text-lg font-semibold">{employee.generalInformation?.preferredFullName}</h2>
        <div className="flex flex-col text-center">
          <span className="font-semibold">
            {hiredDateText}
            {!isFuture ? ' passed' : ''}
          </span>
          {employee.generalInformation?.hiredDate && (
            <div className="text-gray-7">
              from {format(new Date(employee.generalInformation?.hiredDate), 'MMMM dd, yyyy')}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4">
        <div className="flex flex-1 flex-col gap-4">
          <div>
            <Detail label="MED ID" value={employee.id} />
            <Detail label="Employee Code" value={employee.currentMainContract?.employeeCode || EMPTY_DEFAULT} />
          </div>
        </div>

        <div className="flex flex-wrap gap-x-8 gap-y-4">
          <div>
            <div className="font-semibold mb-4">Current Contract & Job</div>
            <div>
              <Detail
                label="Contract Type"
                value={
                  employee.currentMainContract?.contractType
                    ? ContractTypeLabels[employee.currentMainContract.contractType]
                    : EMPTY_DEFAULT
                }
              />
              <Detail label="Company" value={employee.currentMainContract?.company.name} />
            </div>
            <div>
              <Detail label="Level" value={employee.currentPosition?.level} />
              <Detail
                label="Business Unit"
                value={employee.currentPosition?.businessUnit?.name}
                hoverCardContent={
                  employee.currentPosition?.businessUnit?.definitions?.find(it => it.languageCode === 'en')?.content
                }
              />
              <Detail label="Office Location" value={employee.currentPosition?.location.name} />
              <Detail
                label="Department"
                value={employee.currentPosition?.department?.name}
                hoverCardContent={
                  employee.currentPosition?.department?.definitions?.find(it => it.languageCode === 'en')?.content
                }
              />
              <Detail label="Team" value={employee.currentPosition?.team} />
              <Detail
                label="Job"
                value={employee.currentPosition?.job.name}
                hoverCardContent={
                  employee.currentPosition?.job?.definitions?.find(it => it.languageCode === 'en')?.content
                }
              />
              <Detail label="Job Title" value={employee.currentPosition?.jobTitle} />
            </div>
          </div>

          <div>
            <div className="font-semibold mb-4">Current Managerial Accounting</div>
            <div>
              <Detail
                label="VDP"
                value={() => {
                  const list = employee.currentManagerialAccounting?.vdp || [];
                  if (isEmpty(list)) {
                    return null;
                  }
                  return list.map(it => (
                    <div key={it.id}>
                      <span className="tabular-nums">{it.percentage}% - </span>
                      {it.sourceType === EpmVdpSourceType.Regional ? 'Regional' : it.company?.name}
                    </div>
                  ));
                }}
              />
              <Detail
                label="VDD"
                value={() => {
                  const list = employee.currentManagerialAccounting?.vdd || [];
                  if (isEmpty(list)) {
                    return null;
                  }
                  return list.map(it => (
                    <div key={it.id}>
                      <span className="tabular-nums">{it.percentage}% - </span>
                      {it.epmMasterVdd.name}
                    </div>
                  ));
                }}
              />
              <Detail
                label="Functionality"
                value={() => {
                  const list = employee.currentManagerialAccounting?.functionality || [];
                  if (isEmpty(list)) {
                    return null;
                  }
                  return list.map(it => (
                    <div key={it.id}>
                      <span className="tabular-nums">{it.percentage}% - </span>
                      {it.epmMasterFunctionality.name}
                    </div>
                  ));
                }}
              />
              <Detail
                label="Role Type"
                value={() => {
                  const list = employee.currentManagerialAccounting?.roleType || [];
                  if (isEmpty(list)) {
                    return null;
                  }
                  return list.map(it => (
                    <div key={it.id}>
                      <span className="tabular-nums">{it.percentage}% - </span>
                      {it.epmMasterRoleType.name}
                    </div>
                  ));
                }}
              />
              <Detail
                label="Allocation Key"
                value={() => {
                  const list = employee.currentManagerialAccounting?.allocationKey || [];
                  if (isEmpty(list)) {
                    return null;
                  }
                  return list.map(it => (
                    <div key={it.id}>
                      <span className="tabular-nums">{it.percentage}% - </span>
                      {it.epmMasterAllocationKey.name}
                    </div>
                  ));
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
